// Stakeholder data for the WEF Nexus map.
// Researched October 2026 from organisation websites and CORDIS; see each entry's sources.
// identification.confidence: 'low' entries were matched from map location only and should be confirmed.

export const stakeholders = [
  {
    "id": 1,
    "coords": [
      51.986171738249105,
      5.668044626849893
    ],
    "name": "Wageningen University & Research",
    "shortName": "WUR",
    "city": "Wageningen",
    "country": "Netherlands",
    "countryCode": "NL",
    "type": "University",
    "website": "https://www.wur.nl",
    "sectors": [
      "water",
      "food",
      "energy",
      "ecosystems",
      "climate"
    ],
    "summary": "Dutch university and applied research foundation specialising in food, agriculture, water, land use and the environment. It coordinated SIM4NEXUS, one of the flagship EU water-land-food-energy-climate nexus projects, and has partnered in several others, which makes it one of Europe's main nexus research hubs.",
    "nexusWork": [
      {
        "title": "SIM4NEXUS",
        "detail": "Horizon 2020 project on integrated management of the water-land-food-energy-climate nexus for a resource-efficient Europe, using serious-game and model-based policy tools.",
        "years": "2016–2020",
        "role": "coordinator (Stichting Wageningen Research)",
        "url": "https://cordis.europa.eu/project/id/689150"
      },
      {
        "title": "MAGIC – Moving Towards Adaptive Governance in Complexity",
        "detail": "Horizon 2020 project that combined climate, water, land, energy and food analysis to inform EU policy on nexus security.",
        "years": "2016–2020",
        "role": "partner (Wageningen University)",
        "url": "https://cordis.europa.eu/project/id/689669"
      },
      {
        "title": "NEXOGENESIS",
        "detail": "Horizon 2020 project using AI and reinforcement learning to support water-related policies that account for the water-energy-food-ecosystem nexus.",
        "years": "2021–2025",
        "role": "partner (Stichting Wageningen Research)",
        "url": "https://cordis.europa.eu/project/id/101003881"
      },
      {
        "title": "MERLIN",
        "detail": "Horizon 2020 project mainstreaming the restoration of freshwater-related ecosystems at landscape scale.",
        "years": "2021–2026",
        "role": "partner (Wageningen University)",
        "url": "https://cordis.europa.eu/project/id/101036337"
      }
    ],
    "keyProjects": [
      "SIM4NEXUS",
      "MAGIC",
      "NEXOGENESIS",
      "MERLIN",
      "SOLUTIONS"
    ],
    "futureScope": [
      "WUR's Strategic Plan 2025–2028 ('Shape responsible change') names six interconnected themes, including sustainable food systems, sustainable water and land use, climate-proof futures, and biodiversity and resilient ecosystems.",
      "The plan calls for 'comprehensive, interconnected, and systemic' approaches, which fits a continuing nexus role."
    ],
    "governanceRole": [
      "Modelling & decision support",
      "Evidence & monitoring"
    ],
    "sources": [
      {
        "label": "CORDIS – SIM4NEXUS",
        "url": "https://cordis.europa.eu/project/id/689150"
      },
      {
        "label": "CORDIS – MAGIC",
        "url": "https://cordis.europa.eu/project/id/689669"
      },
      {
        "label": "CORDIS – NEXOGENESIS",
        "url": "https://cordis.europa.eu/project/id/101003881"
      },
      {
        "label": "CORDIS – MERLIN",
        "url": "https://cordis.europa.eu/project/id/101036337"
      },
      {
        "label": "WUR Strategic Plan 2025–2028 (PDF)",
        "url": "https://backend.wur.nl/sites/default/files/2025-10/WUR_StrategicPlan_2025-2028_EN.pdf"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "Coordinates fall on Wageningen Campus; matches user hypothesis."
    }
  },
  {
    "id": 2,
    "coords": [
      47.40391513299588,
      8.60984902711207
    ],
    "name": "Eawag – Swiss Federal Institute of Aquatic Science and Technology",
    "shortName": "Eawag",
    "city": "Dübendorf",
    "country": "Switzerland",
    "countryCode": "CH",
    "type": "Research institute",
    "website": "https://www.eawag.ch",
    "sectors": [
      "water",
      "energy",
      "food",
      "ecosystems",
      "climate"
    ],
    "summary": "Eawag is the ETH Domain's water research institute, working on drinking water, wastewater, aquatic ecosystems and water governance. Its work links water with energy (for example, the ecological effects of hydropower) and with food (for example, recovering nutrients from wastewater as fertiliser).",
    "nexusWork": [
      {
        "title": "Aurin / VUNA – fertiliser from urine",
        "detail": "Developed in the VUNA research project, Aurin is a liquid fertiliser recovered from source-separated urine. It was approved by the Swiss Federal Office for Agriculture for edible plants in 2018 and is marketed by the Eawag spin-off Vuna.",
        "years": "2016–present (approval 2018)",
        "role": "developer / host (Process Engineering dept.)",
        "url": "https://eawag.ch/en/department/eng/projects/aurin-fertilisers-from-urine"
      },
      {
        "title": "Hydropeaking mitigation",
        "detail": "FOEN-supported research on how fluctuating hydropower releases affect river habitats, supporting remediation required under the revised Swiss Water Protection Act (about 100 priority plants to be remediated by 2030).",
        "years": "ongoing (dates not stated)",
        "role": "lead (Surface Waters dept.)",
        "url": "https://www.eawag.ch/en/department/surf/projects/hydropeaking-mitigation/"
      },
      {
        "title": "SOLUTIONS",
        "detail": "FP7 project on emerging pollutants in land and water resources management.",
        "years": "2013–2018",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/603437"
      }
    ],
    "keyProjects": [
      "SOLUTIONS",
      "VUNA"
    ],
    "futureScope": [
      "Eawag's Strategic Outlook 2025–2028 sets four priorities: climate change, biodiversity, circular economy (recovering biomass, nutrients and chemicals) and health (resilient WASH systems).",
      "The outlook states that 'water, food, energy, and human health are deeply interconnected' and highlights balancing habitat protection with energy production."
    ],
    "governanceRole": [
      "Innovation & technology",
      "Evidence & monitoring"
    ],
    "sources": [
      {
        "label": "Eawag – Mission and strategy",
        "url": "https://www.eawag.ch/en/about-us/portrait/mission-and-strategy/"
      },
      {
        "label": "Eawag Strategic Outlook (PDF)",
        "url": "https://eawag.ch/fileadmin/Domain1/About/Portraet/Auftrag_Strategie/strategic_outlook_e.pdf"
      },
      {
        "label": "Eawag – Aurin",
        "url": "https://eawag.ch/en/department/eng/projects/aurin-fertilisers-from-urine"
      },
      {
        "label": "Eawag – Hydropeaking mitigation",
        "url": "https://www.eawag.ch/en/department/surf/projects/hydropeaking-mitigation/"
      },
      {
        "label": "CORDIS – SOLUTIONS",
        "url": "https://cordis.europa.eu/project/id/603437"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "Coordinates fall on the Eawag/Empa campus, Überlandstrasse, Dübendorf; matches user hypothesis."
    }
  },
  {
    "id": 3,
    "coords": [
      41.502326879643235,
      2.1037677654082754
    ],
    "name": "Universitat Autònoma de Barcelona – Institute of Environmental Science and Technology",
    "shortName": "UAB / ICTA-UAB",
    "city": "Cerdanyola del Vallès (Bellaterra)",
    "country": "Spain",
    "countryCode": "ES",
    "type": "University",
    "website": "https://www.uab.cat",
    "sectors": [
      "water",
      "energy",
      "food",
      "ecosystems",
      "climate"
    ],
    "summary": "UAB is a public university on the Bellaterra campus near Barcelona. Its Institute of Environmental Science and Technology (ICTA-UAB) is a leading centre for ecological economics. UAB coordinated MAGIC, the Horizon 2020 project on adaptive governance of the water-energy-food nexus, which makes it central to the governance side of nexus research.",
    "nexusWork": [
      {
        "title": "MAGIC – Moving Towards Adaptive Governance in Complexity: Informing Nexus Security",
        "detail": "Horizon 2020 project that combined climate, water, land, energy and food modelling with socio-economic frameworks to inform EU nexus-security policy.",
        "years": "2016–2020",
        "role": "coordinator",
        "url": "https://cordis.europa.eu/project/id/689669"
      },
      {
        "title": "Environmental Justice Atlas (EJAtlas)",
        "detail": "Global atlas of environmental conflicts, including water, energy and land conflicts, launched in 2014 from the EU-funded EJOLT project coordinated by Joan Martinez-Alier's team at ICTA-UAB.",
        "years": "2014–present",
        "role": "coordinator / host",
        "url": "https://www.envjustice.org/2014/03/global-atlas-of-environmental-conflicts-launched-in-brussels/"
      }
    ],
    "keyProjects": [
      "MAGIC",
      "EJOLT"
    ],
    "futureScope": [
      "ICTA-UAB has received its third consecutive María de Maeztu Unit of Excellence accreditation from Spain's State Research Agency, which secures its research programme for a further cycle (years not stated in source)."
    ],
    "governanceRole": [
      "Policy advice",
      "Modelling & decision support"
    ],
    "sources": [
      {
        "label": "CORDIS – MAGIC",
        "url": "https://cordis.europa.eu/project/id/689669"
      },
      {
        "label": "EJAtlas launch (envjustice.org)",
        "url": "https://www.envjustice.org/2014/03/global-atlas-of-environmental-conflicts-launched-in-brussels/"
      },
      {
        "label": "ICTA-UAB María de Maeztu renewal (SOMMA)",
        "url": "https://somma.es/articles/icta-uab-renews-its-maria-de-maeztu-unit-of-excellence-accreditation-for-the-third-time-1345819915004-html/"
      }
    ],
    "identification": {
      "confidence": "medium",
      "note": "Point is on the central UAB Bellaterra campus, about 250 m NE of Plaça Cívica (41.5014, 2.1010 per UAB). It is NOT on CREAF's building (Edifici C, OSM way 123379060, about 41.5009, 2.1093) and not on the ICTA-ICP building. It looks like a generic UAB campus pin. ICTA-UAB is chosen as the nexus-relevant unit because UAB/ICTA coordinated MAGIC; CREAF is a plausible alternative."
    }
  },
  {
    "id": 4,
    "coords": [
      48.06794910984857,
      16.3576687351068
    ],
    "name": "International Institute for Applied Systems Analysis",
    "shortName": "IIASA",
    "city": "Laxenburg",
    "country": "Austria",
    "countryCode": "AT",
    "type": "International organisation",
    "website": "https://iiasa.ac.at",
    "sectors": [
      "water",
      "energy",
      "food",
      "ecosystems",
      "climate"
    ],
    "summary": "IIASA is an independent international research institute founded in 1972. It is based in Schloss Laxenburg near Vienna and funded by national member organisations. It builds open integrated models of water, energy and land systems (for example MESSAGE, GLOBIOM and CWatM) and links them into nexus assessment frameworks used for policy analysis.",
    "nexusWork": [
      {
        "title": "ISWEL – Integrated Solutions for Water, Energy and Land",
        "detail": "Project run with GEF and UNIDO that linked IIASA's ECHO, CWatM, MESSAGE and GLOBIOM models to find cost-effective nexus solutions in the Indus and Zambezi basins. Its scenario explorer was released in 2020.",
        "years": "c. 2017–2020 (workshops from 2017; explorer released 2020)",
        "role": "lead",
        "url": "https://previous.iiasa.ac.at/web/home/research/iswel/ISWEL.html"
      },
      {
        "title": "NEST – NExus Solutions Tool",
        "detail": "Open framework that combines basin-scale infrastructure optimisation for land, water and energy with high-resolution mapping. It was developed through ISWEL and applied to the Indus basin.",
        "years": "from ISWEL (2020 onwards)",
        "role": "developer",
        "url": "https://iiasa.ac.at/models-tools-data/nest"
      },
      {
        "title": "Community Water Model (CWatM)",
        "detail": "Open-source hydrological model of water supply, demand and environmental needs under management and climate change, maintained by IIASA's Water Security group.",
        "years": "2016–present",
        "role": "developer / maintainer",
        "url": "https://cwatm.iiasa.ac.at/1_introduction.html"
      }
    ],
    "keyProjects": [
      "ISWEL",
      "NEST",
      "CWatM"
    ],
    "futureScope": [
      "IIASA's Strategy 2021–2030, 'Reducing footprints, enhancing resilience', puts integrated systems solutions and the SDGs at the centre of its work and adds more social science to its water, energy and climate research."
    ],
    "governanceRole": [
      "Modelling & decision support",
      "Policy advice"
    ],
    "sources": [
      {
        "label": "IIASA – ISWEL",
        "url": "https://previous.iiasa.ac.at/web/home/research/iswel/ISWEL.html"
      },
      {
        "label": "IIASA – NEST",
        "url": "https://iiasa.ac.at/models-tools-data/nest"
      },
      {
        "label": "IAMC – ISWEL scenario explorer",
        "url": "https://iamconsortium.org/resources/database-resources/iswel-nexus-basins-scenario-explorer"
      },
      {
        "label": "CWatM documentation",
        "url": "https://cwatm.iiasa.ac.at/1_introduction.html"
      },
      {
        "label": "IIASA strategy 2021–2030 (blog)",
        "url": "https://iiasa.ac.at/blog/sep-2020/reducing-footprints-and-enhancing-resilience-strategic-direction-for-next-decade"
      },
      {
        "label": "Wikipedia – IIASA",
        "url": "https://en.wikipedia.org/wiki/International_Institute_for_Applied_Systems_Analysis"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "Coordinates fall on Schloss Laxenburg (Blauer Hof), IIASA's seat; matches user hypothesis."
    }
  },
  {
    "id": 5,
    "coords": [
      50.71838394176099,
      7.127399881288146
    ],
    "name": "United Nations University – Institute for Environment and Human Security",
    "shortName": "UNU-EHS",
    "city": "Bonn",
    "country": "Germany",
    "countryCode": "DE",
    "type": "International organisation",
    "website": "https://unu.edu/ehs",
    "sectors": [
      "water",
      "food",
      "climate",
      "ecosystems"
    ],
    "summary": "UNU-EHS is a UN University institute founded in Bonn in 2003. It is based on the UN Campus, which also hosts the UNFCCC secretariat. It researches environmental risks, climate impacts and human security, for example how unsustainable groundwater use for irrigation threatens food and water security.",
    "nexusWork": [
      {
        "title": "Interconnected Disaster Risks report series",
        "detail": "Annual flagship report on how risks are linked. The 2023 edition, 'Risk Tipping Points', includes groundwater depletion driven by agricultural intensification.",
        "years": "2021–2025",
        "role": "lead",
        "url": "https://collections.unu.edu/view/UNU:9288"
      },
      {
        "title": "Technical Report: Groundwater depletion",
        "detail": "2023 technical background report showing how accelerating abstraction for irrigation endangers aquifers that supply drinking water and crops.",
        "years": "2023",
        "role": "author",
        "url": "https://collections.unu.edu/view/UNU:9295"
      },
      {
        "title": "Joint MSc 'Geography of Environmental Risks and Human Security'",
        "detail": "Master's programme run with the University of Bonn, plus PhD and capacity-development courses.",
        "years": "ongoing",
        "role": "co-provider",
        "url": "https://unu.edu/ehs/our-work"
      }
    ],
    "keyProjects": [
      "Interconnected Disaster Risks"
    ],
    "futureScope": [
      "UNU-EHS is organised into two departments: 'Risk and Adaptation' and 'Transformation'. The Transformation department aims to 'catalyse new coalitions and actions for a sustainable and climate resilient world'."
    ],
    "governanceRole": [
      "Policy advice",
      "Participation & capacity building"
    ],
    "sources": [
      {
        "label": "UNU-EHS – Our work",
        "url": "https://unu.edu/ehs/our-work"
      },
      {
        "label": "Interconnected Disaster Risks 2023",
        "url": "https://collections.unu.edu/view/UNU:9288"
      },
      {
        "label": "Groundwater depletion technical report",
        "url": "https://collections.unu.edu/view/UNU:9295"
      },
      {
        "label": "Interconnected Risks – UNU-EHS",
        "url": "https://interconnectedrisks.org/2025/unu-ehs"
      },
      {
        "label": "de.wikipedia – UN-Campus (coordinates)",
        "url": "https://de.wikipedia.org/wiki/UN-Campus"
      }
    ],
    "identification": {
      "confidence": "low",
      "note": "Point is about 130 m E of the UN Campus centroid (50.7182, 7.1255 per Wikipedia), i.e. on the Langer Eugen/UN Campus, and about 370 m from Tulpenfeld (IDOS). The marker identifies the UN Campus, but not which tenant. UNU-EHS is chosen as the research-oriented tenant; UNFCCC or UNCCD (also on campus) are alternatives. Confirm with the author."
    }
  },
  {
    "id": 6,
    "coords": [
      51.483898546482855,
      -3.165929345649384
    ],
    "name": "Natural Resources Wales (Cyfoeth Naturiol Cymru)",
    "shortName": "NRW",
    "city": "Cardiff",
    "country": "United Kingdom",
    "countryCode": "GB",
    "type": "Government agency",
    "website": "https://naturalresources.wales",
    "sectors": [
      "water",
      "energy",
      "food",
      "ecosystems",
      "climate"
    ],
    "summary": "NRW is the Welsh Government-sponsored body for environmental regulation and natural resource management, formed in 2013. Its remit covers water resources, flood risk, forestry, marine licensing and protected sites. Its statutory State of Natural Resources Reports frame the food and energy systems as areas needing transformation, which makes it a governance actor that both implements and regulates.",
    "nexusWork": [
      {
        "title": "State of Natural Resources Report (SoNaRR2020)",
        "detail": "Statutory assessment under the Environment (Wales) Act 2016 of progress towards sustainable management of natural resources. It identifies the food, energy and transport systems as needing transformation.",
        "years": "2020–2021",
        "role": "author (statutory duty)",
        "url": "https://naturalresources.wales/evidence-and-data/research-and-reports/state-of-natural-resources-report-sonarr-for-wales-2020/sonarr2020-introduction/?lang=en"
      },
      {
        "title": "4 Rivers for LIFE (LIFE20 NAT/UK/000100)",
        "detail": "EU LIFE project restoring the Teifi, Cleddau, Tywi and Usk SAC rivers by removing fish barriers and working with about 350 farms to cut nutrient and sediment run-off.",
        "years": "2021–2029",
        "role": "coordinating beneficiary",
        "url": "https://webgate.ec.europa.eu/life/publicWebsite/project/details/5709"
      }
    ],
    "keyProjects": [
      "4 Rivers for LIFE",
      "SoNaRR"
    ],
    "futureScope": [
      "NRW's Corporate Plan to 2030, 'Nature and people thriving together', sets three objectives: nature recovering, communities resilient to climate change, and pollution minimised. It covers flood risk, catchment water quality and renewable energy on the Welsh Government Woodland Estate (250 MW target by 2030), and backs the Sustainable Farming Scheme."
    ],
    "governanceRole": [
      "Implementation & operation",
      "Evidence & monitoring"
    ],
    "sources": [
      {
        "label": "NRW – Corporate plan to 2030",
        "url": "https://naturalresources.wales/corporateplan?lang=en"
      },
      {
        "label": "SoNaRR2020 introduction",
        "url": "https://naturalresources.wales/evidence-and-data/research-and-reports/state-of-natural-resources-report-sonarr-for-wales-2020/sonarr2020-introduction/?lang=en"
      },
      {
        "label": "LIFE database – 4 Rivers for LIFE",
        "url": "https://webgate.ec.europa.eu/life/publicWebsite/project/details/5709"
      },
      {
        "label": "NRW – 4 Rivers for LIFE",
        "url": "https://naturalresources.wales/4RiversforLIFE?lang=en"
      },
      {
        "label": "Wikipedia – Natural Resources Wales",
        "url": "https://en.wikipedia.org/wiki/Natural_Resources_Wales"
      },
      {
        "label": "gov.wales contacts (Tŷ Cambria address, 2024)",
        "url": "https://www.gov.wales/node/58214"
      }
    ],
    "identification": {
      "confidence": "low",
      "note": "The point is on Newport Road/Howard Gardens, Adamsdown. Nearest postcodes (postcodes.io) are residential: Admiral House, Howard Gardens. The closest nexus-relevant site is Tŷ Cambria, 29 Newport Road (about 51.4845, -3.1671, roughly 100 m away), NRW's former Cardiff head office (listed in 2024 Welsh Government contacts). NRW's current office list now shows only St Mellons and Cathays Park. Alternative: Cardiff University School of Engineering, Queen's Buildings (CF24 3AA, about 270 m W). Confirm with the author."
    }
  },
  {
    "id": 7,
    "coords": [
      51.351723679200894,
      12.430924996670917
    ],
    "name": "Helmholtz Centre for Environmental Research – UFZ",
    "shortName": "UFZ",
    "city": "Leipzig",
    "country": "Germany",
    "countryCode": "DE",
    "type": "Research institute",
    "website": "https://www.ufz.de",
    "sectors": [
      "water",
      "food",
      "energy",
      "ecosystems",
      "climate"
    ],
    "summary": "UFZ is Germany's Helmholtz centre for environmental research, with its main site in Leipzig. It studies water resources, land use, ecosystems, chemicals and environment–society interactions. It coordinates EU projects on agricultural water and nutrient retention and partners in nexus policy projects.",
    "nexusWork": [
      {
        "title": "OPTAIN",
        "detail": "Horizon 2020 project on optimal strategies to retain and re-use water and nutrients in small agricultural catchments across Europe's soil-climatic regions.",
        "years": "2020–2026",
        "role": "coordinator",
        "url": "https://cordis.europa.eu/project/id/862756"
      },
      {
        "title": "NEXOGENESIS",
        "detail": "Horizon 2020 project using AI and reinforcement learning to design water policies that account for the water-energy-food-ecosystem nexus.",
        "years": "2021–2025",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/101003881"
      },
      {
        "title": "MERLIN",
        "detail": "Horizon 2020 project on upscaling the restoration of freshwater-related ecosystems at landscape scale.",
        "years": "2021–2026",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/101036337"
      },
      {
        "title": "SOLUTIONS",
        "detail": "FP7 project on present and future emerging pollutants in land and water resources management.",
        "years": "2013–2018",
        "role": "coordinator",
        "url": "https://cordis.europa.eu/project/id/603437"
      }
    ],
    "keyProjects": [
      "OPTAIN",
      "NEXOGENESIS",
      "MERLIN",
      "SOLUTIONS"
    ],
    "futureScope": [
      "Within the Helmholtz programme 'Changing Earth – Sustaining our Future' (2021–2027), UFZ coordinates the topic 'Landscapes of the Future', which focuses on securing terrestrial ecosystems and freshwater resources under global change. It also contributes to topics on sustainable bioeconomy and georesources for the energy transition."
    ],
    "governanceRole": [
      "Evidence & monitoring",
      "Modelling & decision support"
    ],
    "sources": [
      {
        "label": "CORDIS – OPTAIN",
        "url": "https://cordis.europa.eu/project/id/862756"
      },
      {
        "label": "CORDIS – NEXOGENESIS",
        "url": "https://cordis.europa.eu/project/id/101003881"
      },
      {
        "label": "CORDIS – MERLIN",
        "url": "https://cordis.europa.eu/project/id/101036337"
      },
      {
        "label": "CORDIS – SOLUTIONS",
        "url": "https://cordis.europa.eu/project/id/603437"
      },
      {
        "label": "UFZ research programme 2021–2027",
        "url": "https://www.ufz.de/index.php?en=47889"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "Coordinates fall on the UFZ Leipzig campus (Permoserstraße); matches user hypothesis."
    }
  },
  {
    "id": 8,
    "coords": [
      59.81501916490867,
      17.662888306925225
    ],
    "name": "Swedish University of Agricultural Sciences",
    "shortName": "SLU",
    "city": "Uppsala",
    "country": "Sweden",
    "countryCode": "SE",
    "type": "University",
    "website": "https://www.slu.se",
    "sectors": [
      "water",
      "food",
      "energy",
      "ecosystems",
      "climate"
    ],
    "summary": "SLU is Sweden's university for agriculture, forestry, veterinary and environmental science, with its main campus at Ultuna, Uppsala. It also runs national environmental monitoring. It partners in several EU projects on agricultural water and nutrient management, nutrient recycling and freshwater restoration, and runs an urban food-energy-water nexus research theme.",
    "nexusWork": [
      {
        "title": "SLU Urban Futures – Food-Energy-Water nexus theme",
        "detail": "Seed-funding call (2023) under the Ultuna hub for projects on how urban food, energy and water systems interconnect, encouraging partners from municipalities, utilities and food producers.",
        "years": "2023–2024",
        "role": "host / funder",
        "url": "https://www.slu.se/en/Collaborative-Centres-and-Projects/slu-urban-future/funding/food-energy-water-nexus-theme"
      },
      {
        "title": "OPTAIN",
        "detail": "Horizon 2020 project on retaining and re-using water and nutrients in small agricultural catchments.",
        "years": "2020–2026",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/862756"
      },
      {
        "title": "P2GreeN",
        "detail": "Horizon Europe project closing the loop between 'fork and farm' by recycling nutrients from urban wastewater and waste back into agriculture.",
        "years": "2022–2026",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/101081883"
      },
      {
        "title": "MERLIN",
        "detail": "Horizon 2020 project on landscape-scale freshwater ecosystem restoration.",
        "years": "2021–2026",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/101036337"
      }
    ],
    "keyProjects": [
      "OPTAIN",
      "P2GreeN",
      "MERLIN"
    ],
    "futureScope": [
      "SLU's Strategy 2026–2030 states the vision that 'SLU plays a key role in development for sustainable life, based on science and education'. It prioritises next steps towards sustainable development, data-driven research and cross-boundary collaboration, including environmental assessment."
    ],
    "governanceRole": [
      "Evidence & monitoring",
      "Innovation & technology"
    ],
    "sources": [
      {
        "label": "SLU – Food-energy-water nexus theme",
        "url": "https://www.slu.se/en/Collaborative-Centres-and-Projects/slu-urban-future/funding/food-energy-water-nexus-theme"
      },
      {
        "label": "CORDIS – OPTAIN",
        "url": "https://cordis.europa.eu/project/id/862756"
      },
      {
        "label": "CORDIS – P2GreeN",
        "url": "https://cordis.europa.eu/project/id/101081883"
      },
      {
        "label": "CORDIS – MERLIN",
        "url": "https://cordis.europa.eu/project/id/101036337"
      },
      {
        "label": "SLU Strategy 2026–2030",
        "url": "https://internt.slu.se/en/organisation--styrning/mission-vision-values-objectives/slu-strategy-20262030/"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "Coordinates fall on SLU's Ultuna campus, Uppsala; matches user hypothesis."
    }
  },
  {
    "id": 9,
    "coords": [
      53.540431256122005,
      10.004743671676119
    ],
    "name": "HafenCity University Hamburg",
    "shortName": "HCU",
    "city": "Hamburg",
    "country": "Germany",
    "countryCode": "DE",
    "type": "University",
    "website": "https://www.hcu-hamburg.de",
    "sectors": [
      "water",
      "energy",
      "food",
      "climate"
    ],
    "summary": "HCU is Hamburg's university for architecture, civil engineering, urban planning and the built environment. Its research on water-sensitive urban design, urban energy transition and urban–rural nutrient cycles connects the city-scale water, energy and food systems with planning and governance.",
    "nexusWork": [
      {
        "title": "BlueGreenStreets",
        "detail": "BMBF-funded (RES:Z) project on water-sensitive street design that combines rainwater management, urban greening and heat mitigation in German municipalities.",
        "years": "2019–2024",
        "role": "lead",
        "url": "https://www.umweltbundesamt.de/themen/klima-energie/klimafolgen-anpassung/werkzeuge-der-anpassung/tatenbank/bluegreenstreets"
      },
      {
        "title": "P2GreeN",
        "detail": "Horizon Europe project on circular nutrient flows from urban wastewater and waste back to farms.",
        "years": "2022–2026",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/101081883"
      },
      {
        "title": "CARE 4 Hamburg",
        "detail": "Collaborative Alliance for Research on the Energy Transition in Hamburg. HCU's City Science Lab integrates energy-system data and scenarios into the Hamburg Urban Data Platform.",
        "years": "2024–2027",
        "role": "partner",
        "url": "https://forschungsportal.hcu-hamburg.de/en/projects/care-4-hamburg-collaborative-alliance-for-research-on-the-energy-/"
      }
    ],
    "keyProjects": [
      "P2GreeN",
      "BlueGreenStreets",
      "CARE 4 Hamburg",
      "CLEVER Cities"
    ],
    "futureScope": [
      "Running HCU projects extend past 2026: CARE 4 Hamburg (to 2027), PACT – Port City Territories in Action on inclusive energy transition in port cities (2024–2027), and CircleBIM on circular construction (2024–2028)."
    ],
    "governanceRole": [
      "Participation & capacity building",
      "Innovation & technology"
    ],
    "sources": [
      {
        "label": "UBA Tatenbank – BlueGreenStreets",
        "url": "https://www.umweltbundesamt.de/themen/klima-energie/klimafolgen-anpassung/werkzeuge-der-anpassung/tatenbank/bluegreenstreets"
      },
      {
        "label": "CORDIS – P2GreeN",
        "url": "https://cordis.europa.eu/project/id/101081883"
      },
      {
        "label": "HCU research portal – CARE 4 Hamburg",
        "url": "https://forschungsportal.hcu-hamburg.de/en/projects/care-4-hamburg-collaborative-alliance-for-research-on-the-energy-/"
      },
      {
        "label": "HCU – Prof. Knieling research projects",
        "url": "https://www.hcu-hamburg.de/research/arbeitsgebiete-/-professuren/prof-dr-joerg-knieling/forschung"
      },
      {
        "label": "de.wikipedia – HCU (coordinates)",
        "url": "https://de.wikipedia.org/wiki/HafenCity_Universität_Hamburg"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "Wikipedia gives HCU building coordinates 53.5403 N, 10.0048 E, within about 10 m of the marker."
    }
  },
  {
    "id": 10,
    "coords": [
      45.47871913088878,
      9.227296893502318
    ],
    "name": "Politecnico di Milano",
    "shortName": "PoliMi",
    "city": "Milan",
    "country": "Italy",
    "countryCode": "IT",
    "type": "University",
    "website": "https://www.polimi.it",
    "sectors": [
      "water",
      "energy",
      "food",
      "ecosystems",
      "climate"
    ],
    "summary": "PoliMi is Italy's largest technical university. Its Città Studi (Leonardo) campus hosts the environmental and water-systems groups (DEIB, DICA). PoliMi is a European leader in decision-analytic modelling of reservoir operations and water-energy-food trade-offs, and has worked on both European and transboundary nexus projects.",
    "nexusWork": [
      {
        "title": "GoNEXUS",
        "detail": "Horizon 2020 project developing tools and solutions for governing the water-energy-food-ecosystems nexus under global change.",
        "years": "2021–2025",
        "role": "partner (DEIB)",
        "url": "https://cordis.europa.eu/project/id/101003722"
      },
      {
        "title": "DAFNE",
        "detail": "Horizon 2020 project building a decision-analytic framework to explore the water-energy-food nexus in complex, transboundary river basins of fast-growing developing countries.",
        "years": "2016–2020",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/690268"
      },
      {
        "title": "CLINT – CLImate INTelligence",
        "detail": "Horizon 2020 project using machine learning to detect and attribute climate extremes and to support the design of adaptation.",
        "years": "2021–2025",
        "role": "coordinator",
        "url": "https://cordis.europa.eu/project/id/101003876"
      }
    ],
    "keyProjects": [
      "GoNEXUS",
      "DAFNE",
      "CLINT"
    ],
    "futureScope": [
      "PoliMi holds five-year ERC Starting Grants (Horizon Europe) that carry nexus-relevant work forward: HYPOR on nutrient and pollutant dynamics in rivers, and UNITES on energy policies that are resilient to future uncertainty."
    ],
    "governanceRole": [
      "Modelling & decision support"
    ],
    "sources": [
      {
        "label": "CORDIS – GoNEXUS",
        "url": "https://cordis.europa.eu/project/id/101003722"
      },
      {
        "label": "DEIB – GoNEXUS page",
        "url": "https://www.deib.polimi.it/eng/european-projects/details/409"
      },
      {
        "label": "CORDIS – DAFNE",
        "url": "https://cordis.europa.eu/project/id/690268"
      },
      {
        "label": "CORDIS – CLINT",
        "url": "https://cordis.europa.eu/project/id/101003876"
      },
      {
        "label": "PoliMi – 3 ERC Starting Grants",
        "url": "https://www.polimi.it/en/research/news/detail/3-erc-starting-grants-won-at-politecnico-di-milano"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "Coordinates fall on PoliMi's Leonardo campus (Piazza Leonardo da Vinci, Città Studi); matches user hypothesis."
    }
  },
  {
    "id": 11,
    "coords": [
      50.928371368513936,
      6.929360796643978
    ],
    "name": "University of Cologne (Universität zu Köln)",
    "shortName": "UoC",
    "city": "Cologne",
    "country": "Germany",
    "countryCode": "DE",
    "type": "University",
    "website": "https://www.uni-koeln.de",
    "sectors": [
      "energy",
      "water",
      "ecosystems",
      "climate"
    ],
    "summary": "UoC is one of Germany's largest universities, with its main building on Albertus-Magnus-Platz. Its affiliated Institute of Energy Economics (EWI) models European energy markets and the energy transition. It also leads a DFG Collaborative Research Centre on how water scarcity shapes life and landscapes in the driest deserts.",
    "nexusWork": [
      {
        "title": "Institute of Energy Economics (EWI)",
        "detail": "Affiliated institute, founded in 1943, that builds quantitative models of European electricity and gas systems and global energy markets for energy-transition policy.",
        "years": "1943–present",
        "role": "affiliated institute",
        "url": "https://wiso.uni-koeln.de/en/research/affiliated-institutes/institute-of-energy-economics"
      },
      {
        "title": "CRC 1211 'Earth – Evolution at the Dry Limit'",
        "detail": "DFG Collaborative Research Centre led by UoC on how the availability of liquid water constrains biological and landscape evolution in the Atacama and Namib deserts.",
        "years": "2016–present (third phase from Oct 2024)",
        "role": "lead university",
        "url": "https://aei.uni-bonn.de/en/faculty/news/press-archive/2024/crc1211-is-beeing-extended"
      }
    ],
    "keyProjects": [
      "CRC 1211",
      "EWI"
    ],
    "futureScope": [
      "CRC 1211 entered its third DFG funding phase in October 2024 and focuses on how hyper-arid ecosystems and soils co-evolve under climate change.",
      "EWI continues to model the European electricity and gas sectors for energy-transition policy."
    ],
    "governanceRole": [
      "Modelling & decision support",
      "Evidence & monitoring"
    ],
    "sources": [
      {
        "label": "UoC WiSo – EWI",
        "url": "https://wiso.uni-koeln.de/en/research/affiliated-institutes/institute-of-energy-economics"
      },
      {
        "label": "EWI website",
        "url": "https://ewi.uni-koeln.de/en"
      },
      {
        "label": "Uni Bonn – CRC 1211 extended (2024)",
        "url": "https://aei.uni-bonn.de/en/faculty/news/press-archive/2024/crc1211-is-beeing-extended"
      },
      {
        "label": "de.wikipedia – Universität zu Köln (coordinates)",
        "url": "https://de.wikipedia.org/wiki/Universität_zu_Köln"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "Wikipedia gives UoC coordinates 50°55′41″N 6°55′43″E (main building, Albertus-Magnus-Platz), about 50 m from the marker. The identification is certain, but UoC's direct WEF-nexus record is thinner than others. TH Köln's ITT (Deutz) is the stronger nexus actor in Cologne but is about 4 km away, so it is not this point."
    }
  },
  {
    "id": 12,
    "coords": [
      51.52469516995069,
      -0.08879149592015603
    ],
    "name": "C40 Cities Climate Leadership Group",
    "shortName": "C40 Cities",
    "city": "London",
    "country": "United Kingdom",
    "countryCode": "GB",
    "type": "Think tank / NGO",
    "website": "https://www.c40.org",
    "sectors": [
      "water",
      "energy",
      "food",
      "climate"
    ],
    "summary": "C40 is a global network of about 96 major cities, founded in 2005, that helps mayors take joint climate action. Its programmes cover energy and buildings, food systems, and water, heat and nature, which makes it a city-level governance actor for the WEF nexus in Europe and beyond.",
    "nexusWork": [
      {
        "title": "C40 Good Food Cities Declaration",
        "detail": "Signatory cities, including London, Barcelona, Copenhagen, Milan, Oslo, Paris and Stockholm, commit to a 'Planetary Health Diet' for all citizens by 2030. They also commit to halve food loss and waste from a 2015 baseline and to align public food procurement with these goals.",
        "years": "2019–2030",
        "role": "convener / secretariat",
        "url": "https://www.c40.org/news/good-food-cities/"
      },
      {
        "title": "Water Safe Cities Accelerator",
        "detail": "Sixteen cities, including Copenhagen, Lisbon, Milan, Oslo and Rotterdam, commit to early warning and emergency plans for flood and drought by 2027. By 2030 they also commit to a water-resilience pathway: clean water access, flood protection, or net-zero water and wastewater systems.",
        "years": "2023–2030",
        "role": "convener / secretariat",
        "url": "https://www.c40.org/news/c40-cities-launch-water-safe-cities-accelerator/"
      },
      {
        "title": "Energy & Buildings and Food Systems programme areas",
        "detail": "C40's stated goals include halving fossil-fuel use by 2030 and a fair transition to clean energy. 'Energy and Buildings', 'Food Systems' and 'Water, Heat and Nature' are core programme areas.",
        "years": "2005–present",
        "role": "network programme lead",
        "url": "https://www.c40.org/about-c40/"
      }
    ],
    "keyProjects": [
      "Good Food Cities Declaration",
      "Water Safe Cities Accelerator"
    ],
    "futureScope": [
      "The Water Safe Cities Accelerator sets milestones for 2027 (early warning, emergency plans) and 2030 (resilience pathways). These will shape urban water governance in member cities such as Copenhagen, Lisbon, Milan, Oslo and Rotterdam.",
      "The Good Food Cities commitments run to 2030 and tie city food procurement and waste policy to climate targets.",
      "C40's headline goal of halving fossil-fuel use by 2030 keeps urban energy transition at the centre of its agenda."
    ],
    "governanceRole": [
      "Policy advice",
      "Participation & capacity building"
    ],
    "sources": [
      {
        "label": "C40 – About",
        "url": "https://www.c40.org/about-c40/"
      },
      {
        "label": "C40 – Good Food Cities",
        "url": "https://www.c40.org/news/good-food-cities/"
      },
      {
        "label": "C40 – Water Safe Cities Accelerator launch",
        "url": "https://www.c40.org/news/c40-cities-launch-water-safe-cities-accelerator/"
      },
      {
        "label": "Charity Commission – C40 contact information (44 Featherstone Street, EC1Y 8RN)",
        "url": "https://register-of-charities.charitycommission.gov.uk/en/charity-search/-/charity-details/5094016/contact-information"
      },
      {
        "label": "postcodes.io reverse lookup",
        "url": "https://api.postcodes.io/postcodes?lon=-0.08879149592015603&lat=51.52469516995069&limit=10&radius=200"
      }
    ],
    "identification": {
      "confidence": "medium",
      "note": "postcodes.io places the point about 10 m from EC1Y 8RE (38–40 Featherstone St) and 26 m from EC1Y 8RN (44 Featherstone St, 'Script' building). Royal Mail and the Charity Commission list C40 Cities Climate Leadership (UK) at First Floor, 44 Featherstone Street. The Tavistock Institute of Human Relations (50 Featherstone St, about 60 m away) is the only other research body nearby but has a weaker nexus fit."
    }
  },
  {
    "id": 13,
    "coords": [
      59.666438888032864,
      10.774117966548163
    ],
    "name": "Norwegian University of Life Sciences (Norges miljø- og biovitenskapelige universitet)",
    "shortName": "NMBU",
    "city": "Ås",
    "country": "Norway",
    "countryCode": "NO",
    "type": "University",
    "website": "https://www.nmbu.no",
    "sectors": [
      "food",
      "water",
      "energy",
      "ecosystems",
      "climate"
    ],
    "summary": "NMBU is Norway's life-sciences university on the Ås campus south of Oslo, with roots in 1859. Its 2023–2030 strategy aims to 'safeguard the basis for life on Earth' and covers food security, renewable biomass, climate and biodiversity. That makes it a research and education hub for the land, food and bioresource side of the nexus.",
    "nexusWork": [
      {
        "title": "AQUA-FAANG – Advancing European Aquaculture by Genome Functional Annotation",
        "detail": "Horizon 2020 project on genome function in Europe's six main farmed fish species. It supports precision breeding for disease resistance and more sustainable aquaculture food production.",
        "years": "2019–2023",
        "role": "coordinator",
        "url": "https://cordis.europa.eu/project/id/817923"
      },
      {
        "title": "Foods of Norway (Centre for Research-based Innovation)",
        "detail": "Centre developing biorefining methods that turn natural bioresources, such as trees, into feed ingredients for fish and farm animals. This reduces reliance on imported feed protein.",
        "years": "not stated on source page",
        "role": "host",
        "url": "https://www.nmbu.no/fakulteter/fakultet-biovitenskap/foods-norway"
      },
      {
        "title": "BatCAT – Battery Cell Assembly Twin",
        "detail": "Horizon Europe project with 18 partners building a digital twin for manufacturing Li-ion, Na-ion and redox-flow batteries, which are key storage technologies for renewable energy.",
        "years": "2024–2027",
        "role": "coordinator",
        "url": "https://www.nmbu.no/en/research/projects/batcat"
      },
      {
        "title": "CCWater (Erasmus+ capacity building, 'Water Harmony' group)",
        "detail": "Partnership strengthening water-related higher education on water scarcity and climate adaptation with universities in Sri Lanka, China and Mongolia.",
        "years": "2021–2024",
        "role": "partner",
        "url": "https://www.nmbu.no/en/node/47070"
      }
    ],
    "keyProjects": [
      "AQUA-FAANG",
      "BatCAT",
      "Foods of Norway",
      "CCWater"
    ],
    "futureScope": [
      "NMBU's 2023–2030 strategy commits it to the green transition and to food security within ecological limits, with photosynthetic biomass as a key renewable resource.",
      "BatCAT runs until June 2027 and extends NMBU's role into energy-storage manufacturing."
    ],
    "governanceRole": [
      "Evidence & monitoring",
      "Innovation & technology"
    ],
    "sources": [
      {
        "label": "NMBU Strategy 2023–2030",
        "url": "https://www.nmbu.no/forside/strategy-nmbu-2023-2030"
      },
      {
        "label": "NMBU – EU funded research",
        "url": "https://www.nmbu.no/en/node/47283"
      },
      {
        "label": "CORDIS – AQUA-FAANG",
        "url": "https://cordis.europa.eu/project/id/817923"
      },
      {
        "label": "NMBU – BatCAT",
        "url": "https://www.nmbu.no/en/research/projects/batcat"
      },
      {
        "label": "NMBU – Foods of Norway",
        "url": "https://www.nmbu.no/fakulteter/fakultet-biovitenskap/foods-norway"
      },
      {
        "label": "NMBU – CCWater",
        "url": "https://www.nmbu.no/en/node/47070"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "The point lies on the NMBU campus in Ås, as hypothesised. No closer competing organisation was found."
    }
  },
  {
    "id": 14,
    "coords": [
      38.29106659583977,
      21.784712802867112
    ],
    "name": "University of Patras (Panepistimio Patron)",
    "shortName": "UPatras",
    "city": "Patras (Rio)",
    "country": "Greece",
    "countryCode": "GR",
    "type": "University",
    "website": "https://www.upatras.gr",
    "sectors": [
      "water",
      "food",
      "energy",
      "climate"
    ],
    "summary": "The University of Patras is Greece's third-largest university, with its main campus at Rio near Patras. Its engineering and agricultural groups lead EU cross-border projects on rural water scarcity, smart irrigation and precision agrifood. This makes it a regional innovation actor for the Mediterranean water–food nexus.",
    "nexusWork": [
      {
        "title": "IDRON – Innovative Dual sustainable solutions for Rural water scarcity and use OptimizatioN",
        "detail": "Interreg Euro-MED project on innovative irrigation, smart monitoring and new water sources for climate-vulnerable rural Mediterranean areas. Patras leads the project scientifically and contributes to its decision support system.",
        "years": "Interreg Euro-MED 2021–2027 period (exact dates not stated)",
        "role": "lead partner / scientific coordinator",
        "url": "https://idron.interreg-euro-med.eu/?p=4560"
      },
      {
        "title": "TAGsTwo",
        "detail": "Interreg VI-A Greece–Italy project using drones, sensors, AI, decision support systems and precision farming to build a greener and more resilient agrifood ecosystem. Pilots run in Western Greece and Calabria, and a cross-border innovation hub is planned.",
        "years": "2021–2027 programme; kick-off Sept 2025",
        "role": "lead partner",
        "url": "https://www.greece-italy.eu/the-tagstwo-project-officially-kicked-off-in-patras/"
      },
      {
        "title": "RE-WATER – Eco technologies for wastewater management",
        "detail": "Interreg Greece–Italy project on wastewater reuse technologies and their managerial and social conditions. Partners include the Municipality of Patras and its water utility.",
        "years": "2014–2020 programme",
        "role": "partner",
        "url": "https://re-water.chemeng.upatras.gr/en/node/5"
      }
    ],
    "keyProjects": [
      "IDRON",
      "TAGsTwo",
      "RE-WATER"
    ],
    "futureScope": [
      "As lead partner of TAGsTwo (launched in Sept 2025 under the 2021–2027 programme), Patras is building a cross-border agrifood and precision-farming innovation hub with Calabria.",
      "Through IDRON it develops decision support for rural irrigation and water scarcity under climate change."
    ],
    "governanceRole": [
      "Innovation & technology",
      "Modelling & decision support"
    ],
    "sources": [
      {
        "label": "IDRON – UPatras partner page",
        "url": "https://idron.interreg-euro-med.eu/?p=4560"
      },
      {
        "label": "IDRON – project home",
        "url": "https://idron.interreg-euro-med.eu/"
      },
      {
        "label": "Interreg Greece–Italy – TAGsTwo kick-off",
        "url": "https://www.greece-italy.eu/the-tagstwo-project-officially-kicked-off-in-patras/"
      },
      {
        "label": "RE-WATER project",
        "url": "https://re-water.chemeng.upatras.gr/en/node/5"
      },
      {
        "label": "keep.eu – University of Patras projects",
        "url": "https://keep.eu/partners/1921"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "The point lies on the University of Patras campus at Rio, as hypothesised."
    }
  },
  {
    "id": 15,
    "coords": [
      40.84548634535332,
      14.25774233838745
    ],
    "name": "University of Naples Federico II (Università degli Studi di Napoli Federico II)",
    "shortName": "UNINA",
    "city": "Naples",
    "country": "Italy",
    "countryCode": "IT",
    "type": "University",
    "website": "https://www.unina.it",
    "sectors": [
      "water",
      "energy",
      "food",
      "climate",
      "ecosystems"
    ],
    "summary": "Federico II is one of Italy's largest universities. It was a partner in the H2020 nexus-governance project MAGIC and was the national hub of Italy's AGRITECH centre for agricultural technologies. Its work spans nexus accounting, agri-food innovation and energy recovery in water networks.",
    "nexusWork": [
      {
        "title": "MAGIC – Moving Towards Adaptive Governance in Complexity: Informing Nexus Security",
        "detail": "H2020 project coordinated by UAB that tested EU policies with the MuSIASEM nexus-accounting approach. The Naples team built model-to-model translation and solver tools for the 'nexus complex'.",
        "years": "2016–2020",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/689669"
      },
      {
        "title": "AGRITECH – National Research Centre for Agricultural Technologies",
        "detail": "A centre of about €350 million under Italy's Recovery and Resilience Plan (PNRR), bringing together 28 universities, 5 research centres and 18 companies. It works on climate resilience, low-impact agriculture, circularity and food traceability.",
        "years": "2022–2025",
        "role": "hub coordinator",
        "url": "https://www.cmcc.it/projects/agritech-national-research-centre-for-agricultural-technologies"
      },
      {
        "title": "REDAWN – Reducing Energy Dependency in Atlantic Area Water Networks",
        "detail": "Interreg Atlantic Area project installing micro-hydropower at pressure points in irrigation, public supply, industrial and wastewater networks to recover energy.",
        "years": "2017–2020",
        "role": "partner",
        "url": "https://keep.eu/projects/19377"
      },
      {
        "title": "WAGON2Africa – Water Energy Food Nexus 2 Africa",
        "detail": "PNRR-funded cooperation of ten Italian universities, led by Politecnico di Torino, building WEF-nexus partnerships with African universities.",
        "years": "2024–2026",
        "role": "partner",
        "url": "https://www.luiss.it/en/international/international-projects/water-energy-food-nexus-2-africa-wagon2africa"
      }
    ],
    "keyProjects": [
      "MAGIC",
      "AGRITECH",
      "REDAWN",
      "WAGON2Africa"
    ],
    "futureScope": [
      "Naples' recent nexus work (MAGIC, AGRITECH, WAGON2Africa) ended by early 2026, and no verified Federico II nexus project running beyond 2026 was found. Forward-looking statements should be checked with the university.",
      "WAGON2Africa and AGRITECH position Naples as a bridge between Italian agri-tech research and African WEF-nexus partnerships."
    ],
    "governanceRole": [
      "Modelling & decision support",
      "Innovation & technology"
    ],
    "sources": [
      {
        "label": "CORDIS – MAGIC",
        "url": "https://cordis.europa.eu/project/id/689669"
      },
      {
        "label": "IRIS UNINA – Semantic model-to-model translation to capture the nexus (MAGIC)",
        "url": "https://www.iris.unina.it/handle/11588/779794"
      },
      {
        "label": "CMCC – AGRITECH",
        "url": "https://www.cmcc.it/projects/agritech-national-research-centre-for-agricultural-technologies"
      },
      {
        "label": "keep.eu – REDAWN",
        "url": "https://keep.eu/projects/19377"
      },
      {
        "label": "LUISS – WAGON2Africa",
        "url": "https://www.luiss.it/en/international/international-projects/water-energy-food-nexus-2-africa-wagon2africa"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "The point lies at Federico II's central seat (Corso Umberto I), as hypothesised. This is the institutional pin rather than a specific department."
    }
  },
  {
    "id": 16,
    "coords": [
      37.97817799132635,
      23.783803862300708
    ],
    "name": "National Technical University of Athens (Ethniko Metsovio Polytechneio)",
    "shortName": "NTUA",
    "city": "Athens (Zografou)",
    "country": "Greece",
    "countryCode": "GR",
    "type": "University",
    "website": "https://www.ntua.gr",
    "sectors": [
      "water",
      "energy",
      "food",
      "ecosystems",
      "climate"
    ],
    "summary": "NTUA is Greece's leading engineering university, based on the Zografou campus in Athens. It coordinates major EU demonstrations of circular water–energy–food solutions and climate resilience in the Mediterranean, and it was a partner in the WEFE-nexus policy project NEXOGENESIS.",
    "nexusWork": [
      {
        "title": "NEXOGENESIS",
        "detail": "H2020 project using AI and reinforcement learning to assess the water–energy–food–ecosystems nexus and design better water-related policies. Case studies include the Nestos basin in Greece.",
        "years": "2021–2025",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/101003881"
      },
      {
        "title": "HYDROUSA – Demonstration of water loops with innovative regenerative business models for the Mediterranean region",
        "detail": "H2020 demonstrations on Mykonos, Tinos and Lesvos of nature-based, decentralised water loops. They recover water, nutrients and energy for local agriculture and tourism.",
        "years": "2018–2023",
        "role": "coordinator",
        "url": "https://cordis.europa.eu/project/id/776643"
      },
      {
        "title": "CARDIMED – Climate Adaptation and Resilience Demonstrated in the MEDiterranean region",
        "detail": "Horizon Europe project building a Mediterranean climate-resilience framework. It combines nature-based solutions, digital infrastructure and holistic water–energy–food–ecosystem modelling across demonstration sites.",
        "years": "2023–2028",
        "role": "coordinator",
        "url": "https://cordis.europa.eu/project/id/101112731"
      },
      {
        "title": "WEF nexus teaching (ITIA, Dept. of Water Resources & Environmental Engineering)",
        "detail": "Course notes on research methods, questions and databases for the water–energy–food nexus (Sargentis & Iliopoulou).",
        "years": "2025",
        "role": "author / educator",
        "url": "https://www.itia.ntua.gr/en/docinfo/2566"
      }
    ],
    "keyProjects": [
      "NEXOGENESIS",
      "HYDROUSA",
      "CARDIMED"
    ],
    "futureScope": [
      "CARDIMED, coordinated by NTUA, runs until February 2028 and will scale up nature-based climate-adaptation and WEFE modelling across Mediterranean regions.",
      "HYDROUSA's circular water-loop demonstrations give NTUA a base for replicating nexus solutions on water-scarce islands and coasts."
    ],
    "governanceRole": [
      "Innovation & technology",
      "Modelling & decision support"
    ],
    "sources": [
      {
        "label": "CORDIS – NEXOGENESIS",
        "url": "https://cordis.europa.eu/project/id/101003881"
      },
      {
        "label": "CORDIS – HYDROUSA",
        "url": "https://cordis.europa.eu/project/id/776643"
      },
      {
        "label": "CORDIS – CARDIMED",
        "url": "https://cordis.europa.eu/project/id/101112731"
      },
      {
        "label": "Circle the Med – Prof. Simos Malamis (HYDROUSA, CARDIMED coordinator)",
        "url": "https://www.circlethemed.com/persons/prof-simos-malamis"
      },
      {
        "label": "ITIA – WEF nexus methodology notes",
        "url": "https://www.itia.ntua.gr/en/docinfo/2566"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "The point lies on the NTUA Zografou campus, as hypothesised."
    }
  },
  {
    "id": 17,
    "coords": [
      43.2966416344366,
      -2.8706922614805723
    ],
    "name": "AZTI (Fundación AZTI – AZTI Fundazioa)",
    "shortName": "AZTI",
    "city": "Derio (Bizkaia Science & Technology Park)",
    "country": "Spain",
    "countryCode": "ES",
    "type": "Technology centre",
    "website": "https://www.azti.es",
    "sectors": [
      "food",
      "water",
      "ecosystems",
      "climate"
    ],
    "summary": "AZTI is a Basque technology centre for marine and food research and a member of the Basque Research and Technology Alliance (BRTA). It has sites in Sukarrieta, Pasaia and Derio. It links sustainable fisheries, aquaculture and food-industry innovation with ecosystem protection.",
    "nexusWork": [
      {
        "title": "FutureFoodS – European partnership for a sustainable Future of Food Systems",
        "detail": "Horizon Europe co-funded partnership with 82 participants mobilising research for circular, healthy food systems that stay within planetary boundaries.",
        "years": "2024–2034",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/101136361"
      },
      {
        "title": "LIFE BREWERY",
        "detail": "LIFE project demonstrating full recovery of brewery by-products as aquaculture-feed ingredients, linking food-industry waste and water/energy efficiency with fish-feed supply.",
        "years": "2017–2020",
        "role": "coordinator",
        "url": "https://lifebrewery.azti.es/en/?p=600"
      }
    ],
    "keyProjects": [
      "FutureFoodS",
      "LIFE BREWERY"
    ],
    "futureScope": [
      "Through the FutureFoodS partnership, which runs to 2034, AZTI will help shape EU research calls on sustainable food systems.",
      "As a BRTA member, AZTI states it aims to remain the ally of the food and marine sectors while 'guaranteeing the protection of ecosystems'."
    ],
    "governanceRole": [
      "Innovation & technology",
      "Evidence & monitoring"
    ],
    "sources": [
      {
        "label": "AZTI – contact (Derio site, Google Maps pin 43.29891, -2.87044)",
        "url": "https://www.azti.es/en/contact/"
      },
      {
        "label": "AZTI – About",
        "url": "https://www.azti.es/en/?p=4827"
      },
      {
        "label": "AZTI joins BRTA",
        "url": "https://www.azti.es/en/azti-is-reinventing-itself-to-cope-with-the-challenges-of-the-food-and-maritime-value-chains"
      },
      {
        "label": "CORDIS – FutureFoodS",
        "url": "https://cordis.europa.eu/project/id/101136361"
      },
      {
        "label": "LIFE BREWERY",
        "url": "https://lifebrewery.azti.es/en/?p=600"
      },
      {
        "label": "NEIKER – contact (Derio centre 43.291191, -2.873241)",
        "url": "https://neiker.eus/en/contact/"
      }
    ],
    "identification": {
      "confidence": "low",
      "note": "The point is inside Bizkaia Science & Technology Park (Derio) but does not sit on any building whose coordinates could be verified. AZTI's own Google Maps pin for its Derio site (Astondo Bidea, Edificio 609) is about 250 m north at the same longitude. NEIKER's Derio centre (C/ Berreaga 1, by NEIKER's own coordinates) is about 620 m south. Tecnalia (Astondo Bidea, Edificio 700) and CIC bioGUNE (Edificio 801A) are also in the park, but their exact coordinates could not be verified. AZTI is chosen as the nearest verified candidate. NEIKER (agricultural research, closer to the hypothesis) and Tecnalia are the alternatives. Please confirm with the author."
    }
  },
  {
    "id": 18,
    "coords": [
      41.52065346021219,
      -4.721571173220508
    ],
    "name": "Fundación CARTIF",
    "shortName": "CARTIF",
    "city": "Boecillo (Valladolid)",
    "country": "Spain",
    "countryCode": "ES",
    "type": "Technology centre",
    "website": "https://www.cartif.es",
    "sectors": [
      "energy",
      "water",
      "food",
      "ecosystems",
      "climate"
    ],
    "summary": "CARTIF is a private non-profit technology centre in the Boecillo Technology Park near Valladolid. It works on energy efficiency, agrifood biotechnology, wastewater treatment and nature-based solutions. It has coordinated several large EU projects that link urban energy and water management with sustainable food production.",
    "nexusWork": [
      {
        "title": "URBAN GreenUP – New Strategy for Re-Naturing Cities through Nature-Based Solutions",
        "detail": "H2020 project co-developing 'Renaturing Urban Plans' for climate mitigation, adaptation and efficient water management. Nature-based solutions were demonstrated in Valladolid, Liverpool, Izmir and five other cities.",
        "years": "2017–2023",
        "role": "coordinator",
        "url": "https://cordis.europa.eu/project/id/730426"
      },
      {
        "title": "REMOURBAN – REgeneration MOdel for accelerating the smart URBAN transformation",
        "detail": "H2020 smart-city lighthouse project in Valladolid, Nottingham and Tepebaşı/Eskişehir integrating energy, mobility and ICT. It retrofitted more than 1,000 dwellings.",
        "years": "2015–2020",
        "role": "coordinator",
        "url": "https://cordis.europa.eu/project/id/646511"
      },
      {
        "title": "CIRAWA – Agro-ecological strategies for resilient farming in West Africa",
        "detail": "Horizon Europe project combining indigenous and scientific knowledge for food security and climate resilience in Cape Verde, Ghana, Senegal and The Gambia. It covers integrated irrigation and fertilisation, biofertilisers, and soil and water management.",
        "years": "2023–2027",
        "role": "coordinator",
        "url": "https://cordis.europa.eu/project/id/101084398"
      }
    ],
    "keyProjects": [
      "URBAN GreenUP",
      "REMOURBAN",
      "CIRAWA"
    ],
    "futureScope": [
      "CIRAWA, coordinated by CARTIF, runs until June 2027 and extends its agro-ecology, irrigation and soil–water management work to West African food systems.",
      "CARTIF's portfolio, from urban nature-based solutions to agrifood biotechnology and wastewater treatment, positions it to transfer nexus technologies to industry and municipalities."
    ],
    "governanceRole": [
      "Innovation & technology",
      "Implementation & operation"
    ],
    "sources": [
      {
        "label": "EU Industrial Ecosystems Monitor – Fundación CARTIF (address: Parque Tecnológico de Boecillo 205)",
        "url": "https://monitor-industrial-ecosystems.ec.europa.eu/technology-centre/fundacion-cartif"
      },
      {
        "label": "CORDIS – URBAN GreenUP",
        "url": "https://cordis.europa.eu/project/id/730426"
      },
      {
        "label": "CORDIS – REMOURBAN",
        "url": "https://cordis.europa.eu/project/id/646511"
      },
      {
        "label": "CORDIS – CIRAWA",
        "url": "https://cordis.europa.eu/project/id/101084398"
      },
      {
        "label": "Agronews Castilla y León – CIRAWA kick-off at CARTIF",
        "url": "https://www.agronewscastillayleon.com/el-proyecto-cirawa-celebra-en-cartif-su-reunion-de-lanzamiento-para-impulsar-la-agro-ecologia-en"
      }
    ],
    "identification": {
      "confidence": "medium",
      "note": "The point is in Boecillo Technology Park, and CARTIF's address is Parque Tecnológico de Boecillo, parcela 205. CARTIF's own site could not be fetched, so the exact building could not be checked. CARTIF is by far the most nexus-relevant tenant (CIDAUT, an automotive centre, is another)."
    }
  },
  {
    "id": 19,
    "coords": [
      53.34453638826205,
      -6.26083796255469
    ],
    "name": "Trinity College Dublin, The University of Dublin",
    "shortName": "TCD",
    "city": "Dublin",
    "country": "Ireland",
    "countryCode": "IE",
    "type": "University",
    "website": "https://www.tcd.ie",
    "sectors": [
      "water",
      "energy",
      "food",
      "climate"
    ],
    "summary": "Trinity College Dublin is Ireland's oldest university. Its School of Engineering hosts a Water–Energy Nexus research group (Prof. Aonghus McNabola) that recovers energy from water and wastewater systems. It has led cross-border EU projects on micro-hydropower and net-zero water services.",
    "nexusWork": [
      {
        "title": "Dŵr Uisce",
        "detail": "Interreg Ireland–Wales project with Bangor University on the water–energy nexus. It installed micro-hydropower at water-treatment sites (up to 20% energy reduction) and wastewater heat recovery in commercial kitchens and food production.",
        "years": "Interreg Ireland–Wales 2014–2020; final event Feb 2023",
        "role": "lead partner",
        "url": "https://www.tcd.ie/mecheng/news/2023/the-dr-uisce-project--final-event-delivering-solutions-for-the-sustainability-of-the-water-energy-nexus/"
      },
      {
        "title": "REDAWN – Reducing Energy Dependency in Atlantic Area Water Networks",
        "detail": "€2.9 million Interreg Atlantic Area project installing micro-hydropower at pressure points in drinking water, irrigation and wastewater networks in IE, UK, FR, ES and PT.",
        "years": "2017–2020",
        "role": "partner (technical lead)",
        "url": "https://www.tcd.ie/news_events/articles/new-29m-eu-project-will-boost-energy-efficiency-and-cut-water-costs/"
      },
      {
        "title": "TRACE – Transforming Rural water communities As positive Climate and Energy districts",
        "detail": "EPA-funded project creating water–energy data hubs, combining renewable energy and storage, and using AI to optimise rural water services.",
        "years": "2023–2025",
        "role": "lead",
        "url": "https://www.epa.ie/our-services/research/epa-funded-research/epa-funded-projects/research-climate-data-table-dev/transforming-rural-water-communities-as-positive-climate-and-energy-districts-trace.php"
      }
    ],
    "keyProjects": [
      "Dŵr Uisce",
      "REDAWN",
      "TRACE"
    ],
    "futureScope": [
      "Trinity's E3 initiative (Engineering, Environment and Emerging Technologies) aims to integrate engineering, natural and computer sciences to 'design and actively shape the planet's natural capital'. Its Martin Naughton E3 Learning Foundry is being built under Project Ireland 2040.",
      "TCD's water–energy group is working toward net-zero water systems through energy recovery and digital tools, as in Dŵr Uisce and TRACE."
    ],
    "governanceRole": [
      "Innovation & technology",
      "Evidence & monitoring"
    ],
    "sources": [
      {
        "label": "TCD Civil Engineering – Water-Energy Nexus",
        "url": "https://www.tcd.ie/civileng/research/environment/water/water-energy-nexus/"
      },
      {
        "label": "TCD – Dŵr Uisce final event",
        "url": "https://www.tcd.ie/mecheng/news/2023/the-dr-uisce-project--final-event-delivering-solutions-for-the-sustainability-of-the-water-energy-nexus/"
      },
      {
        "label": "TCD – REDAWN announcement",
        "url": "https://www.tcd.ie/news_events/articles/new-29m-eu-project-will-boost-energy-efficiency-and-cut-water-costs/"
      },
      {
        "label": "keep.eu – REDAWN",
        "url": "https://keep.eu/projects/19377"
      },
      {
        "label": "EPA – TRACE",
        "url": "https://www.epa.ie/our-services/research/epa-funded-research/epa-funded-projects/research-climate-data-table-dev/transforming-rural-water-communities-as-positive-climate-and-energy-districts-trace.php"
      },
      {
        "label": "TCD – E3",
        "url": "https://www.tcd.ie/e3/"
      }
    ],
    "identification": {
      "confidence": "medium",
      "note": "The point is on Dame Street / College Green, about 130 m west of Trinity's Front Gate and about 400 m from the campus centre. It is not on a campus building. The other pins are building-accurate, so this one may be approximate. No other nexus-relevant organisation was found at this address. Please confirm with the author."
    }
  },
  {
    "id": 20,
    "coords": [
      52.489575129251904,
      13.321990481401526
    ],
    "name": "Ecologic Institute (Ecologic Institut gemeinnützige GmbH)",
    "shortName": "Ecologic",
    "city": "Berlin",
    "country": "Germany",
    "countryCode": "DE",
    "type": "Think tank / NGO",
    "website": "https://www.ecologic.eu",
    "sectors": [
      "water",
      "food",
      "ecosystems",
      "climate",
      "energy"
    ],
    "summary": "Ecologic Institute is an independent, non-profit environmental policy think tank founded in 1995 in Berlin, with more than 100 staff. It works across water, agriculture, biodiversity, climate and energy policy. It often coordinates EU projects that turn research into policy recommendations for EU water, nature-restoration and food-system governance.",
    "nexusWork": [
      {
        "title": "AQUACROSS – Knowledge, Assessment, and Management for AQUAtic Biodiversity and Ecosystem Services aCROSS EU policies",
        "detail": "H2020 project advancing ecosystem-based management of freshwater, coastal and marine systems to meet the EU 2020 Biodiversity Strategy targets.",
        "years": "2015–2018",
        "role": "coordinator",
        "url": "https://cordis.europa.eu/project/id/642317"
      },
      {
        "title": "MERLIN – Mainstreaming Ecological Restoration of freshwater-related ecosystems in a Landscape context",
        "detail": "H2020 Green Deal project upscaling freshwater-ecosystem restoration across Europe.",
        "years": "2021–2026",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/101036337"
      },
      {
        "title": "BALANCE – Biodiversity and Landscape Restoration through Agricultural Systems for Nature Conservation and Resilient Farm Economies",
        "detail": "Horizon Europe project showing how farming can be nature-positive and economically viable. It supports implementation of the EU Nature Restoration Regulation in six regions.",
        "years": "2026–2030",
        "role": "coordinator",
        "url": "https://www.ecologic.eu/20485"
      },
      {
        "title": "TrueFoodS – Transforming European Food Systems",
        "detail": "FutureFoodS-partnership project on making Europe's post-harvest food systems more sustainable, equitable and resilient. Ecologic leads the legal and policy analysis.",
        "years": "2026–2029",
        "role": "work package lead",
        "url": "https://www.ecologic.eu/20415"
      }
    ],
    "keyProjects": [
      "AQUACROSS",
      "MERLIN",
      "BALANCE",
      "TrueFoodS"
    ],
    "futureScope": [
      "Ecologic coordinates BALANCE (2026–2030), which links agricultural land use with implementation of the EU Nature Restoration Regulation.",
      "Through TrueFoodS (2026–2029) it shapes the legal and policy framework for more resilient post-harvest food systems.",
      "Its newer projects on carbon farming (CREDIBLE 2.0) and agroforestry value chains (AF4VALUE) run to 2029."
    ],
    "governanceRole": [
      "Policy advice",
      "Participation & capacity building"
    ],
    "sources": [
      {
        "label": "Ecologic – About",
        "url": "https://www.ecologic.eu/about"
      },
      {
        "label": "Ecologic – Contact (Pfalzburger Strasse 43/44, 10717 Berlin)",
        "url": "https://www.ecologic.eu/contact"
      },
      {
        "label": "Kauperts – Pfalzburger Straße 43-44 geo-coordinates (52.489307, 13.3211332)",
        "url": "https://berlin.kauperts.de/eintrag/Radio-B2-Pfalzburger-Strasse-43-44-10717-Berlin"
      },
      {
        "label": "CORDIS – AQUACROSS",
        "url": "https://cordis.europa.eu/project/id/642317"
      },
      {
        "label": "CORDIS – MERLIN",
        "url": "https://cordis.europa.eu/project/id/101036337"
      },
      {
        "label": "Ecologic – BALANCE",
        "url": "https://www.ecologic.eu/20485"
      },
      {
        "label": "Ecologic – TrueFoodS",
        "url": "https://www.ecologic.eu/20415"
      },
      {
        "label": "Ecologic – projects list",
        "url": "https://www.ecologic.eu/projects"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "Ecologic's address is Pfalzburger Straße 43/44, 10717 Berlin. A Berlin directory places that building at 52.4893, 13.3211, about 70 m from the point."
    }
  },
  {
    "id": 21,
    "coords": [
      60.20389195696393,
      24.960829968436073
    ],
    "name": "Finnish Meteorological Institute (Ilmatieteen laitos)",
    "shortName": "FMI",
    "city": "Helsinki",
    "country": "Finland",
    "countryCode": "FI",
    "type": "Government agency",
    "website": "https://en.ilmatieteenlaitos.fi",
    "sectors": [
      "climate",
      "water",
      "energy",
      "food"
    ],
    "summary": "FMI is Finland's national weather, climate, marine and space research agency under the Ministry of Transport and Communications. It is based in the Dynamicum building on the Kumpula campus. It provides climate data, forecasts and scenarios that energy, agriculture and water managers use to plan under climate change.",
    "nexusWork": [
      {
        "title": "C3S Clim4Energy – Climate change indicators for the energy sector",
        "detail": "Copernicus Climate Change Service project producing pan-European, energy-relevant climate indicators.",
        "years": "2016–2018",
        "role": "co-developer",
        "url": "https://en.ilmatieteenlaitos.fi/climate-service-centre-ended-projects"
      },
      {
        "title": "ILMAPUSKURI – Climate change adaptation in agriculture",
        "detail": "Research for the Ministry of Agriculture and Forestry on reducing farm vulnerability and improving resilience to climate variability.",
        "years": "2013–2016",
        "role": "research partner",
        "url": "https://en.ilmatieteenlaitos.fi/climate-service-centre-ended-projects"
      },
      {
        "title": "Solar and wind power production forecasts",
        "detail": "Free public forecasts turning predicted sunshine and wind into kWh output, so households and communities can time consumption to renewable supply.",
        "years": "2023–present",
        "role": "service provider",
        "url": "https://en.ilmatieteenlaitos.fi/news/6NXrgLYcqcDTI8di2kM6Ju"
      },
      {
        "title": "PERU-AQUAFUTURA – Decision support for sustainable water resource management",
        "detail": "Academy of Finland project for which FMI supplied hydro-climatic variability analysis and climate scenarios for Peru.",
        "years": "2012–2016",
        "role": "partner",
        "url": "https://en.ilmatieteenlaitos.fi/climate-service-centre-ended-projects"
      }
    ],
    "keyProjects": [
      "C3S Clim4Energy",
      "ILMAPUSKURI",
      "PERU-AQUAFUTURA"
    ],
    "futureScope": [
      "FMI's 2026–2029 strategy aims to use artificial intelligence for more accurate and tailored forecasts. It also plans stronger partnerships with the food, forestry and energy sectors.",
      "FMI's renewable-power forecasts and sector climate indicators feed directly into energy and agricultural adaptation planning."
    ],
    "governanceRole": [
      "Evidence & monitoring",
      "Modelling & decision support"
    ],
    "sources": [
      {
        "label": "FMI – About us (strategy 2026–2029)",
        "url": "https://en.ilmatieteenlaitos.fi/about-us"
      },
      {
        "label": "FMI – Recently ended climate service projects",
        "url": "https://en.ilmatieteenlaitos.fi/climate-service-centre-ended-projects"
      },
      {
        "label": "FMI – Solar and wind power forecasts (12 June 2023)",
        "url": "https://en.ilmatieteenlaitos.fi/news/6NXrgLYcqcDTI8di2kM6Ju"
      },
      {
        "label": "Wikipedia – Dynamicum",
        "url": "https://en.wikipedia.org/wiki/Dynamicum"
      },
      {
        "label": "latitude.to – Dynamicum coordinates (60.2037, 24.9611)",
        "url": "https://latitude.to/articles-by-country/fi/finland/397881/dynamicum"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "The point is about 30 m from the Dynamicum building on the Kumpula campus (60.2037, 24.9611). Dynamicum houses FMI (Erik Palménin aukio 1), not University of Helsinki faculties."
    }
  },
  {
    "id": 22,
    "coords": [
      52.163684089350326,
      21.046272694873565
    ],
    "name": "Warsaw University of Life Sciences – SGGW (Szkoła Główna Gospodarstwa Wiejskiego w Warszawie)",
    "shortName": "SGGW",
    "city": "Warsaw",
    "country": "Poland",
    "countryCode": "PL",
    "type": "University",
    "website": "https://www.sggw.edu.pl",
    "sectors": [
      "water",
      "food",
      "ecosystems",
      "climate"
    ],
    "summary": "SGGW is a major Polish agricultural and life-sciences university, on the Ursynów campus in Warsaw. Its environmental engineering institutes and Water Centre work on catchment hydrology, small water retention, soil health and freshwater restoration. It takes part in several EU water and agriculture projects.",
    "nexusWork": [
      {
        "title": "OPTAIN – OPtimal strategies to retAIN and re-use water and nutrients in small agricultural catchments",
        "detail": "H2020 project, coordinated by UFZ, modelling natural and small water retention measures with SWAT+ and SWAP across 14 European agricultural catchments. Its database of measures is now part of WOCAT.",
        "years": "2020–2026",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/862756"
      },
      {
        "title": "MERLIN – Mainstreaming Ecological Restoration of freshwater-related ecosystems",
        "detail": "H2020 Green Deal project upscaling freshwater-ecosystem restoration in landscapes across Europe.",
        "years": "2021–2026",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/101036337"
      },
      {
        "title": "LivingSoiLL – Healthy Soil to Permanent Crops Living Labs",
        "detail": "Horizon Europe (Mission Soil) project running five living labs to cut erosion, improve soil structure and reduce fertiliser and pesticide impacts in permanent crops.",
        "years": "2024–2028",
        "role": "partner",
        "url": "https://cordis.europa.eu/project/id/101157502"
      },
      {
        "title": "SGGW Water Centre",
        "detail": "Inter-institute centre opened in 2010 with laboratories, a demonstration 'Water Park' on the water cycle and hydrotechnics, and a weather station. It supports research, teaching and public outreach.",
        "years": "2010–present",
        "role": "host",
        "url": "https://cw.sggw.edu.pl/home-page/about-the-water-centre/?lang=en"
      }
    ],
    "keyProjects": [
      "OPTAIN",
      "MERLIN",
      "LivingSoiLL"
    ],
    "futureScope": [
      "LivingSoiLL runs until November 2028 and keeps SGGW active in EU Mission Soil living labs on soil health in agricultural landscapes.",
      "OPTAIN's tools for natural and small water retention, now in WOCAT, support future agricultural water-retention planning in Poland and Europe."
    ],
    "governanceRole": [
      "Evidence & monitoring",
      "Modelling & decision support"
    ],
    "sources": [
      {
        "label": "CORDIS – OPTAIN",
        "url": "https://cordis.europa.eu/project/id/862756"
      },
      {
        "label": "CORDIS – OPTAIN results article",
        "url": "https://cordis.europa.eu/article/id/466277-optimising-water-retention-solutions-across-diverse-agronomic-contexts"
      },
      {
        "label": "CORDIS – MERLIN",
        "url": "https://cordis.europa.eu/project/id/101036337"
      },
      {
        "label": "CORDIS – LivingSoiLL",
        "url": "https://cordis.europa.eu/project/id/101157502"
      },
      {
        "label": "SGGW Water Centre",
        "url": "https://cw.sggw.edu.pl/home-page/about-the-water-centre/?lang=en"
      }
    ],
    "identification": {
      "confidence": "high",
      "note": "The point lies on the SGGW Ursynów campus (Nowoursynowska Street), as hypothesised."
    }
  }
];

export const projects = [
  {
    "id": "MERLIN",
    "name": "MERLIN",
    "full": "Mainstreaming Ecological Restoration of freshwater-related ecosystems in a Landscape context",
    "programme": "Horizon 2020",
    "years": "2021–2026",
    "members": [
      1,
      7,
      8,
      20,
      22
    ],
    "theme": "ecosystems",
    "desc": "Upscales the restoration of freshwater-related ecosystems at landscape scale across Europe.",
    "url": "https://cordis.europa.eu/project/id/101036337"
  },
  {
    "id": "MAGIC",
    "name": "MAGIC",
    "full": "Moving Towards Adaptive Governance in Complexity: Informing Nexus Security",
    "programme": "Horizon 2020",
    "years": "2016–2020",
    "members": [
      3,
      1,
      15
    ],
    "theme": "governance",
    "desc": "Combined climate, water, land, energy and food analysis with socio-economic frameworks to inform EU nexus-security policy. Coordinated by UAB.",
    "url": "https://cordis.europa.eu/project/id/689669"
  },
  {
    "id": "NEXOGENESIS",
    "name": "NEXOGENESIS",
    "full": "Facilitating the next generation of effective and intelligent water-related policies",
    "programme": "Horizon 2020",
    "years": "2021–2025",
    "members": [
      1,
      7,
      16
    ],
    "theme": "water",
    "desc": "Uses AI and reinforcement learning to design water-related policies that account for the water–energy–food–ecosystems nexus.",
    "url": "https://cordis.europa.eu/project/id/101003881"
  },
  {
    "id": "OPTAIN",
    "name": "OPTAIN",
    "full": "OPtimal strategies to retAIN and re-use water and nutrients in small agricultural catchments",
    "programme": "Horizon 2020",
    "years": "2020–2026",
    "members": [
      7,
      8,
      22
    ],
    "theme": "food",
    "desc": "Models natural and small water-retention measures in 14 European agricultural catchments. Coordinated by UFZ.",
    "url": "https://cordis.europa.eu/project/id/862756"
  },
  {
    "id": "SOS-Water",
    "name": "SOS-Water",
    "full": "Water Resources System Safe Operating Space in a Changing Climate and Society",
    "programme": "Horizon Europe",
    "years": "2022–2026",
    "members": [
      4,
      10,
      2
    ],
    "theme": "water",
    "desc": "Defines a safe operating space for Europe's water-resources system under climate and societal change. Coordinated by IIASA.",
    "url": "https://iiasa.ac.at/projects/sos-water"
  },
  {
    "id": "P2GreeN",
    "name": "P2GreeN",
    "full": "Closing the gap between fork and farm for circular nutrient flows",
    "programme": "Horizon Europe",
    "years": "2022–2026",
    "members": [
      9,
      8
    ],
    "theme": "food",
    "desc": "Recycles nutrients from urban wastewater and waste back into agriculture.",
    "url": "https://cordis.europa.eu/project/id/101081883"
  },
  {
    "id": "AQUACROSS",
    "name": "AQUACROSS",
    "full": "Knowledge, Assessment and Management for AQUAtic Biodiversity and Ecosystem Services aCROSS EU policies",
    "programme": "Horizon 2020",
    "years": "2015–2018",
    "members": [
      20,
      1,
      2
    ],
    "theme": "ecosystems",
    "desc": "Advanced ecosystem-based management of freshwater, coastal and marine systems. Coordinated by Ecologic Institute.",
    "url": "https://cordis.europa.eu/project/id/642317"
  },
  {
    "id": "SOLUTIONS",
    "name": "SOLUTIONS",
    "full": "Solutions for present and future emerging pollutants in land and water resources management",
    "programme": "FP7",
    "years": "2013–2018",
    "members": [
      7,
      2,
      1
    ],
    "theme": "water",
    "desc": "Tackled emerging pollutants in land and water resources management. Coordinated by UFZ.",
    "url": "https://cordis.europa.eu/project/id/603437"
  },
  {
    "id": "REDAWN",
    "name": "REDAWN",
    "full": "Reducing Energy Dependency in Atlantic Area Water Networks",
    "programme": "Interreg Atlantic Area",
    "years": "2017–2020",
    "members": [
      19,
      15
    ],
    "theme": "energy",
    "desc": "Installed micro-hydropower at pressure points in drinking-water, irrigation and wastewater networks to recover energy.",
    "url": "https://keep.eu/projects/19377"
  },
  {
    "id": "BALANCE",
    "name": "BALANCE",
    "full": "Biodiversity and Landscape Restoration through Agricultural Systems for Nature Conservation and Resilient Farm Economies",
    "programme": "Horizon Europe",
    "years": "2026–2030",
    "members": [
      20,
      1
    ],
    "theme": "food",
    "desc": "Shows how farming can be nature-positive and economically viable, supporting the EU Nature Restoration Regulation in six regions. Coordinated by Ecologic Institute.",
    "url": "https://www.ecologic.eu/20485"
  }
];
